import { IScore } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import { parseBarnHuntTime } from "../helpers/time";

/** Height division from height_at_withers (inches) */
export const bhGetHeightDivision = (heightAtWithers?: number): string => {
  if (!heightAtWithers) return "M";
  if (heightAtWithers <= 14) return "S";
  if (heightAtWithers <= 18) return "M";
  return "L";
};

/**
 * Calculate barn hunt placement for a single score row, given all scores.
 * Returns the 1-based placement string, or "" if no placement applies.
 *
 * Rules (mirrors backend bhGetPlacement in score-util.ts):
 *  - Instinct / Crazy 8s → no placement
 *  - A-level classes + Master B → ranked within height division (S/M/L)
 *  - All other B-level classes + Line Drive → ranked across all heights
 *  - Only qualified scores receive a placement
 */
export const bhCalculatePlacement = (
  row: IScore,
  allScores: IScore[]
): string => {
  const level = row.dog_class?.level;
  const section = row.dog_class?.section;

  // No placement for Instinct or Crazy 8s
  if (
    level === Constants.dog_class_element_level.barn_hunt_instinct ||
    level === Constants.dog_class_element_level.barn_hunt_crazy_8s
  ) {
    return "";
  }

  // Only qualified scores get a placement
  if (row.barn_hunt?.result_type !== Constants.score_result_type.qualified) {
    return "";
  }

  // Competitive group: same show + same element/level/section
  const group = allScores.filter(
    (s) =>
      s.show_type === Constants.show_type.barn_hunt &&
      s.show_id?.toString() === row.show_id?.toString() &&
      s.dog_class?.show_element === row.dog_class?.show_element &&
      s.dog_class?.level === level &&
      (s.dog_class?.section || "") === (section || "") &&
      s.barn_hunt?.result_type === Constants.score_result_type.qualified
  );

  // A-level classes and Master B → narrow to same height division
  const isMasterB =
    level === Constants.dog_class_element_level.barn_hunt_master &&
    section === "B";
  const useHeightDivision = section === "A" || isMasterB;

  let competitors = group;
  if (useHeightDivision) {
    const myDivision = bhGetHeightDivision(row.height_at_withers);
    competitors = group.filter(
      (s) => bhGetHeightDivision(s.height_at_withers) === myDivision
    );
  }

  // Sort by time ascending (slower/no-time → bottom)
  competitors = [...competitors].sort((a, b) => {
    const tA = parseBarnHuntTime(a.barn_hunt?.time) ?? 999999;
    const tB = parseBarnHuntTime(b.barn_hunt?.time) ?? 999999;
    return tA - tB;
  });

  const idx = competitors.findIndex(
    (s) => s._id?.toString() === row._id?.toString()
  );
  return idx >= 0 ? String(idx + 1) : "";
};
